import React from 'react';
import { CyclingEvent, PROMO, activePromotion, eventCardModel, splitMonYear } from '@radevents/shared';
import { buildHeroSvg } from '../utils/eventHero';
import BookmarkIcon from './BookmarkIcon';

/**
 * Schlanker Bild-Header einer Event-Card (Design „kompakte Event-Cards"). Für die Events liegen
 * keine Fotos vor und KI-Bilder sind unerwünscht — der Kopf trägt sich komplett aus einem
 * generierten, nach Disziplin getönten Bergkamm-Motiv (`buildHeroSvg`). Es wird bewusst NIE ein
 * echtes Foto gezeigt; der Header ist bewusst niedrig, damit die ähnlichen Motive das Grid nicht
 * dominieren.
 *
 * Inhalt: Datum (links, inline), rechts der Countdown („in 28 Tagen") und das Merken-Herz.
 * Kategorie·Eventart und Status stehen im weißen Karten-Body (EventCardBody).
 *
 * Der Countdown stand bis 09/2026 in Berry hinter der Ortszeile. Rückmeldung aus dem Test: dort
 * passt er nicht hin, und rot muss er auch nicht sein. Die Angabe bleibt trotzdem in der Card
 * (die Vorbereitungszeit zählt bei der Auswahl), jetzt als stille Pille neben dem Herz.
 * **Ausnahme gebuchte Card:** dort belegt die Pflicht-Kennzeichnung den Platz neben dem Herz, und
 * auf einem 360er-Phone reicht die Zeile nicht für Datum, Countdown, Herz und Kennzeichnung. Der
 * Countdown bleibt dann in der Ortszeile (EventCardBody), ebenfalls ohne Berry.
 *
 * Bei einer laufenden bezahlten Hervorhebung (siehe `activePromotion`) tritt an die Stelle des
 * Motivs ein Farbverlauf und rechts neben das Herz die Pflicht-Kennzeichnung. Die Maße bleiben
 * exakt gleich — die Hervorhebung darf den Rhythmus des Rasters nicht brechen. Die Hülle drumherum
 * (Rand, Grund, Schatten) macht die Klasse `promoCardClass` an der Card, siehe App.css.
 *
 * Das **Datumsformat** steht nicht mehr hier, sondern in `eventCardDate` (`shared/`) — die App
 * zeichnet dasselbe Modell. Vorher standen Monatsnamen und Range-Logik in beiden Projekten, und
 * die App schrieb den Wochentag in eine zweite Zeile, wo das Web ihn inline führte.
 */

/** Monat fett, Jahr normal. Sonst liest sich „AUG 26" als 26. August (Nutzer-Feedback 07/2026). */
const MonYear: React.FC<{ mon: string }> = ({ mon }) => {
  const { month, year } = splitMonYear(mon);
  return (
    <span className="eh-mon">
      {month}
      {year && <> <span className="eh-yr">{year}</span></>}
    </span>
  );
};

const SCRIM = 'linear-gradient(90deg, rgba(20,14,18,0.6) 0%, rgba(20,14,18,0.12) 55%, rgba(20,14,18,0.3) 100%)';

interface Props {
  event: CyclingEvent;
  favorited: boolean;
  favoriteTitle?: string;
  onFavorite?: (e: React.MouseEvent) => void;
  /**
   * Vorschau: die Card wird nur gezeigt, nicht bedient (Buchungs- und Admin-Dialog). Das Herz
   * bleibt sichtbar — sonst sähe die Vorschau anders aus als das Ergebnis —, ist aber kein Knopf
   * mehr. Sonst lägen im Dialog fokussierbare Elemente, die nirgendwohin führen.
   */
  preview?: boolean;
}

const EventCardHero: React.FC<Props> = ({ event, favorited, favoriteTitle, onFavorite, preview }) => {
  const promo = activePromotion(event);
  // Bei einer Buchung steht er in der Ortszeile, siehe oben (`countdownSlot`).
  const m = eventCardModel(event);
  const date = m.date;
  const countdown = m.countdownSlot === 'head' ? m.countdown : null;

  return (
    <div className="event-hero" style={{ position: 'relative' }}>
      {promo ? (
        /* Gebuchter Kopf: Farbverlauf statt Motiv. Kein Scrim — der Verlauf ist von sich aus
           dunkel genug, ein zweiter Layer darüber würde ihn nur trüben. */
        <div className="eh-motif" style={{ backgroundImage: promo.tier === 'FRAME' ? PROMO.frameHead : PROMO.invertedHead }} />
      ) : (
        <>
          {/* Motiv in eigenem Layer, damit ein abgesagtes Event komplett entsättigt (grau) werden
              kann, ohne Schrift/Herz darüber mit zu vergrauen. */}
          <div className="eh-motif" style={{ backgroundImage: buildHeroSvg(event), ...(event.cancelled ? { filter: 'grayscale(0.92)' } : {}) }} />
          <div className="eh-scrim" style={{ background: SCRIM }} />
        </>
      )}

      <div className="eh-date">
        {date ? (
          <>
            <span className="eh-day">{date.day}</span>
            <MonYear mon={date.mon} />
            {date.second && (
              <>
                <span className="eh-mon">–</span>
                <span className="eh-day">{date.second.day}</span>
                <MonYear mon={date.second.mon} />
              </>
            )}
            {date.weekday && <span className="eh-wd">· {date.weekday}</span>}
          </>
        ) : (
          /* Permanent: kein Termin, also kein Datum-Kopf — das Badge nennt die Saison. */
          <span className="eh-permanent">{m.permanentLabel}</span>
        )}
      </div>

      {/* Das Werbelabel belegt im Entwurf den Platz des Herzens. Merken muss aber erreichbar
          bleiben, deshalb steht das Herz — wie im Handoff vorgeschlagen — links neben dem Label.
          Wird es eng, kürzt der Wochentag (`.eh-wd`) und nicht die Kennzeichnung. */}
      <div className="eh-actions">
        {countdown && <span className="eh-countdown">{countdown}</span>}
        {preview ? (
          <span className="eh-heart" aria-hidden>
            <BookmarkIcon filled={favorited} size={15} />
          </span>
        ) : (
          <button
            className="eh-heart"
            onClick={onFavorite}
            title={favoriteTitle}
            aria-label={favoriteTitle}
          >
            <BookmarkIcon filled={favorited} size={15} />
          </button>
        )}

        {promo && (
          <span className={`eh-promo eh-promo--${promo.tier === 'FRAME' ? 'frame' : 'inverted'}`}>
            <span className="eh-promo-mark" aria-hidden />
            {/* Der Text ist pro Buchung frei — für Screenreader steht deshalb unabhängig davon
                ausgeschrieben davor, worum es sich handelt. Sichtbar bleibt das gebuchte Wording. */}
            <span className="sr-only">Bezahlte Hervorhebung: </span>
            {promo.label}
          </span>
        )}
      </div>
    </div>
  );
};

export default EventCardHero;
